import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { RowClickedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { IRole } from '~/types/role'

import columns from './columns'
import CreateRoleModal from './CreateRoleModal'
import CustomTooltip from './CustomTooltip'

import ContentLayout from '~/components/ContentLayout'
import Layout from '~/components/Layouts/Layout'
import TableWrapper from '~/components/Tables/TableWrapper'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useTouchDown } from '~/hooks/useTouchDown'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { keys as rolesKeys, useRolesListQuery } from '~/queries/user-roles'
import { dateSort } from '~/utils/date'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const RoleListing = (): ReactElement => {
  /** Local state */

  const roleEditionAuthorized = useIsUserAuthorized([AUTHORIZATIONS.AUTH.ROLES.WRITE])

  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const gridRef = useRef<AgGridReact<IRole>>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const [isModalOpen, setModalOpen] = useState(false)

  const { isTouchDown, setIsTouchDown } = useTouchDown('Meta')

  /** Queries & Mutations */

  const { data: roles } = useRolesListQuery()

  const sortRoles = roles?.data?.sort(({ updated_at_ts: aUpdatedDate }, { updated_at_ts: bUpdatedDate }) =>
    dateSort(aUpdatedDate, bUpdatedDate, 'desc')
  )

  /** Effects */

  useDocumentTitle('IAM Roles')

  /** Handlers */

  const handleOpenModal = (isOpen: boolean) => () => setModalOpen(isOpen)

  const handleSuccess = () => {
    setModalOpen(false)
    queryClient.invalidateQueries({ queryKey: rolesKeys.list() })
    displayToast('Role successfully created', 'success')
  }

  const handleFailure = (key?: string) => {
    setModalOpen(false)

    switch (key) {
      case 'mwm.com/mwm-auth/internal/db.roleWrapper.txNameAlreadyUsed':
        displayToast('Role could not be created; role name is already used', 'error')
        break
      default:
        displayToast('Role could not be created', 'error')
    }
  }

  const handleRowClicked = (evt: RowClickedEvent) => {
    if (isTouchDown) {
      window.open(`/mwm-users/roles/${evt.data.role_id}`, '_blank', 'rel=noopener noreferrer')
      setIsTouchDown(false)
    } else {
      navigate(`/mwm-users/roles/${evt.data.role_id}`)
    }
  }

  /** Render */

  return (
    <>
      <Layout className="roles-view" withPadding>
        <ViewTitle className="layout-view-title">IAM Roles</ViewTitle>
        <ContentLayout>
          <TableWrapper
            addButton
            addButtonDisabled={!roleEditionAuthorized}
            addButtonLabel="New role"
            addButtonTooltip={!roleEditionAuthorized ? 'unauthorized' : undefined}
            className="roles-table with-min-height"
            columnDefs={columns}
            components={{ customTooltip: CustomTooltip }}
            entriesName="roles"
            onAddButtonClick={handleOpenModal(true)}
            onRowClicked={handleRowClicked}
            ref={gridRef}
            rowData={sortRoles}
            tableRef={tableRef}
            withPagination={false}
            withSearchBar
          />
        </ContentLayout>
      </Layout>
      {roleEditionAuthorized ? (
        <CreateRoleModal
          isOpen={isModalOpen}
          onClose={handleOpenModal(false)}
          onFailure={handleFailure}
          onSuccess={handleSuccess}
        />
      ) : null}
    </>
  )
}

export default RoleListing
